import { memo, useCallback } from 'react';
import { isEmpty } from 'lodash';
import Link from 'next/link';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import COLOR from '../../../../shared/constant/colorset';
import ProductCardImage
  from '../../../../shared/components/product/card/ProductCardImage';
import ProductCardFunction
  from '../../../../shared/components/product/card/ProductCardFunction';
import ProductCardSticker
  from '../../../../shared/components/product/card/ProductCardSticker';
import ProductCardPrice
  from '../../../../shared/components/product/card/ProductCardPrice';
import { PRODUCT_PATH } from '../../../../shared/constant';
import { ProductStatus } from '../../../../shared/interfaces';
import ProductCardTag from './ProductCardTag';
import ProductCardDeliveryType from './ProductCardDeliveryType';
import applyImageUrlFormat
  from '../../../../shared/utils/image/imaegUrlformatter';
import { ProductSelectData } from '../../../../shared/interfaces/Product';

const Container = styled.div`
  flex: 0 0 338px;
  height: 682px;
  margin-right: 16px;
  color: ${COLOR.kurlyGray800};
  cursor: pointer;

  :nth-of-type(3n) {
    margin-right: 0;
  }
`;

const Card = styled(ProductCardImage)`
  width: 338px;
  height: 435px;
`;

const ProductCardImageWrap = styled.div<{ isDim: boolean; }>`
  ${({ isDim }) => isDim && css`
    ${Card} {
      background-color: ${COLOR.kurlyBlack};
    }
  `};
  ${({ isDim }) => isDim && css`
    > div > img {
      opacity: 0.6;
    }
  `}
`;

const CardFunction = styled(ProductCardFunction)`
  right: 16px;
  bottom: 16px;
  i {
    width: 45px;
    height: 45px;
  }
`;

const Sticker = styled(ProductCardSticker)`
  display: flex;
  align-items: center;
  padding: 0 10px 0 8px;
  height: 32px;
`;

const Content = styled.div`
  padding: 14px 10px 0 0;
`;

const Name = styled.div`
  overflow: hidden;
  max-height: 58px;
  margin-bottom: 9px;
  padding: 0 0 0;
  font-size: 20px;
  line-height: 29px;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  letter-spacing: normal;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const Price = styled(ProductCardPrice)`
  .discount-rate, .sales-price {
    font-weight: 700;
    font-size: 18px;
    line-height: 26px;
    white-space: nowrap;
    letter-spacing: -0.5px;
  }
  .discount-rate {
    margin-right: 7px;
  }
  .dimmed-price {
    padding-top: 2px;
    font-size: 16px;
    color: ${COLOR.kurlyGray400};
    line-height: 24px;
    text-decoration: line-through;
  }
`;

const ShortDescription = styled.p`
  overflow: hidden;
  padding-top: 12px;
  font-size: 13px;
  color: ${COLOR.kurlyGray450};
  line-height: 18px;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  letter-spacing: normal;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const SoldOutTitle = styled.strong`
  display: block;
  font-size: 28px;
  color: ${COLOR.kurlyWhite};
  line-height: 40px;
`;

const SoldOutDesc = styled.span`
  display: block;
  margin-bottom: -6px;
  padding: 9px 16px 0;
  font-size: 18px;
  color: ${COLOR.kurlyWhite};
  line-height: 23px;
`;

const SoldOutLayer = styled.div<{ isPurchasable: boolean; }>`
  position: absolute;
  left: 0;
  top: 50%;
  width: 100%;
  margin-top: -5px;
  text-align: center;
  transform: translate(0, -50%);
  letter-spacing: -0.5px;
  ${({ isPurchasable }) => isPurchasable && `
    margin-top: -1px;
    ${SoldOutTitle} {
        font-weight: 400;
        font-size: 18px;
        padding: 0 80px;
        line-height: 23px;
      }
  `}
`;

interface Props {
  index: number;
  productNo: number;
  imageUrl: string;
  name: string;
  price: number;
  discount?: {
    price: number;
    rate: number;
  };
  sticker?: {
    backgroundColor: string;
    opacity: number;
    contents: {
      text: string;
      weight: string;
    }[];
  };
  status: ProductStatus;
  isGroupProduct: boolean;
  canRestockNotify: boolean;
  deliveryTypeNames: string[];
  shortDescription: string;
  isBuyNow: boolean;
  tags?: {
    name: string;
    type: string;
  }[];
  isMultiplePrice?: boolean;
  queryId?: string;
  handleLinkClick(selectLink: ProductSelectData): void;
  selectProduct(selectShortcut: ProductSelectData): void;
}

function ProductCard({
  index, productNo, imageUrl, price, discount, name, sticker,
  status, isGroupProduct, canRestockNotify,
  deliveryTypeNames, shortDescription, tags, isMultiplePrice, isBuyNow, queryId,
  handleLinkClick, selectProduct,
}: Props) {
  const link = `${PRODUCT_PATH.detail.uri}/${productNo}`;
  const { code, message } = status;
  const isSoldOut = code === 'SOLD_OUT' || code === 'NOT_PURCHASABLE';
  const thumbFormatUrl = applyImageUrlFormat(imageUrl, { width: 676, format: 'auto' });

  const handleShortcut = useCallback(() => {
    const selectShortcut = {
      index, productNo, isGroupProduct,
    };
    selectProduct(selectShortcut);
  }, [index, isGroupProduct, productNo, selectProduct]);

  return (
    <Link href={link}>
      <Container onClick={() => handleLinkClick({ index, productNo, isGroupProduct })}>
        <ProductCardImageWrap isDim={isSoldOut}>
          <Card imageUrl={thumbFormatUrl}>
            {sticker &&
              <Sticker
                backgroundColor={sticker.backgroundColor}
                opacity={sticker.opacity}
                contents={sticker.contents}
              />
            }
            <CardFunction
              contentProductNo={productNo}
              code={code}
              isGroupProduct={isGroupProduct}
              deliveryTypeNames={deliveryTypeNames}
              queryId={queryId}
              canRestockNotify={canRestockNotify}
              isBuyNow={isBuyNow}
              onSelectProductShortcut={handleShortcut}
            />
            {isSoldOut &&
              <SoldOutLayer isPurchasable={code === 'NOT_PURCHASABLE'}>
                <SoldOutTitle>{message?.title || 'Coming Soon'}</SoldOutTitle>
                <SoldOutDesc>{message?.content}</SoldOutDesc>
              </SoldOutLayer>
            }
          </Card>
        </ProductCardImageWrap>
        <Content>
          {!isEmpty(deliveryTypeNames) &&
            <ProductCardDeliveryType
              deliveryTypeNames={deliveryTypeNames}
            />
          }
          <Name>{name}</Name>
          <Price
            price={price}
            discount={discount}
            isMultiplePrice={isMultiplePrice}
          />
          {shortDescription && <ShortDescription>{shortDescription}</ShortDescription>}
          {tags && !isEmpty(tags) &&
            <ProductCardTag
              tags={tags}
            />
          }
        </Content>
      </Container>
    </Link>
  );
}

export default memo(ProductCard);
